import { Button, Divider, Modal, notification, Image } from 'antd'
import { ColumnProps, TableProps } from 'antd/lib/table'
import axios from 'axios'
import React, { useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { IPaginatedListAPI } from '../list/models/IPaginatedListAPI'
import PaginatedList from '../list/PaginatedList'
import { IGift } from './models'


export const GiftList: React.FC<TableProps<IGift>> = () => {
  const endpoint = `/api/gifts`
  const listRef = useRef<IPaginatedListAPI>(null)

  const removeGift = useCallback(async (id: string) => {
    try {
      await axios.delete(`${endpoint}/${id}`)

      const { current } = listRef

      if (current) {
        current.refetchData()
      }
    } catch (error) {
      notification.error({
        message: 'Something went wrong',
      })
    }
  }, [endpoint])

  const showRemoveGiftModal = useCallback((record: IGift) => {
    Modal.confirm({
      cancelText: 'No',
      content: 'You won\'t be able to revert the gift.',
      okText: 'Yes',
      okType: 'danger',
      onOk() {
        removeGift(record._id)
      },
      title: `Are you sure to delete ${record.name}`
    })
  }, [removeGift])

  const renderListItemOptions = useCallback((_: any, record: IGift) => {
    return (
      <div>
        <Button type='link' className="px-0">
          <Link to={`${record._id}`}>
            Edit
          </Link>
        </Button>
        <Divider type="vertical" />
        <Button
          type='link'
          className="text-danger px-0"
          onClick={() => { showRemoveGiftModal(record) }}
        >
          Delete
        </Button>
      </div>
    )
  }, [showRemoveGiftModal])

  const renderImage = useCallback((imgUrl?: string) => {
    if (!imgUrl) return null
    
    // Handle both old API URLs and new MinIO URLs
    let displayUrl = imgUrl
    
    // If it's an old API URL, convert to MinIO public URL
    if (imgUrl.includes('/api/gifts/serve-image/')) {
      const pathParts = imgUrl.split('/api/gifts/serve-image/')[1]
      displayUrl = `https://data4.ikara.co/data/minio/ikara-data/uploads/${pathParts}`
    }
    
    // If it's an old MinIO API URL, convert to public URL
    if (imgUrl.includes('data4.ikara.co:9000/ikara-data/')) {
      const pathParts = imgUrl.split('data4.ikara.co:9000/ikara-data/')[1]
      displayUrl = `https://data4.ikara.co/data/minio/ikara-data/${pathParts}`
    }
    
    return (
      <Image
        src={displayUrl}
        alt="Gift image"
        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
        onError={(e) => {
          console.error('Image load error:', e)
          console.error('Image URL:', displayUrl)
        }}
      />
    )
  }, [])

  const columns: Array<ColumnProps<IGift>> = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Name'
    },
    {
      dataIndex: 'description',
      key: 'description',
      title: 'Description'
    },
    {
      dataIndex: 'amount',
      key: 'amount',
      title: 'Stock'
    },
    {
      dataIndex: 'cost',
      key: 'cost',
      title: 'Cost'
    },
    {
      key: 'image',
      title: 'Image',
      render: (_, record) => renderImage(record.imgUrl),
      width: '80px'
    },
    {
      key: 'action',
      render: renderListItemOptions,
      title: 'Options'
    }
  ]

  return (
    <PaginatedList<IGift>
      columns={columns}
      endpoint={endpoint}
      pageSize={10}
      getAPIRef={listRef}
    />
  )
}
